import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Icons,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import useIsOverflowing from '@app/hooks/useIsOverflowing';
import { createStyles } from './AssignmentSelection.styles';

const MENU_ID = 'destination-assignment-menu';

type OptionLabelProps = {
  text: string;
  sx?: any;
  isMobile?: boolean;
};

const OptionLabel = ({ text, sx, isMobile }: OptionLabelProps) => {
  // Check if text is overflowing
  const [textRef, isOverflowing] = useIsOverflowing<HTMLSpanElement>();

  return (
    <Tooltip
      title={text}
      disableInteractive
      placement="top"
      disableHoverListener={!isOverflowing || isMobile}
      disableFocusListener={!isOverflowing || isMobile}
      disableTouchListener={!isOverflowing || isMobile}
    >
      <Box component="span" ref={textRef} sx={sx}>
        {text}
      </Box>
    </Tooltip>
  );
};

type AssignmentSelectionProps = {
  assignments: {
    key: string;
    value: string | number;
    label: string | number;
  }[];
  onChange: (id: string) => void;
  value: string;
};

const AssignmentSelection = ({
  assignments,
  value,
  onChange,
}: AssignmentSelectionProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const styles = createStyles(theme, isDark);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const selectedLabel = useMemo(() => {
    const match = assignments?.find(
      (assignment) => String(assignment?.value) === String(value)
    );
    return match ? String(match.label) : '';
  }, [assignments, value]);

  const openMenu = () => {
    setAnchorEl(triggerRef.current);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  return (
    <Box id="select-assignment" ref={triggerRef} sx={{ width: '100%' }}>
      <TextField
        label="Destination assignment"
        value={selectedLabel}
        onClick={openMenu}
        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openMenu();
          }
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            openMenu();
          }
        }}
        sx={styles.selectField}
        rightElement={
          <Icons.ArrowDropDownOutlined
            fontSize="small"
            aria-hidden
            sx={{
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }}
          />
        }
      />
      <Menu
        id={MENU_ID}
        compact
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          root: {
            disableScrollLock: true,
          },
          paper: {
            style: {
              ...styles.menuPaper,
            },
          },
          list: {
            role: 'listbox',
            'aria-label': 'Destination assignment',
          },
        }}
        sx={styles.menu}
      >
        {assignments?.map((assignment) => (
          <MenuItem
            key={assignment?.key}
            label={
              <OptionLabel
                text={String(assignment?.label)}
                sx={styles.optionLabelSx}
                isMobile={isMobile}
              />
            }
            role="option"
            aria-selected={String(assignment?.value) === String(value)}
            selected={String(assignment?.value) === String(value)}
            onClick={() => {
              onChange(String(assignment?.value));
              closeMenu();
            }}
          />
        ))}
      </Menu>
    </Box>
  );
};

export default React.memo(AssignmentSelection);
